import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center border font-medium transition-colors select-none",
  {
    variants: {
      variant: {
        default: "bg-secondary text-secondary-foreground border-border",
        primary: "bg-primary text-primary-foreground border-transparent",
        // Success semantic
        success:
          "bg-success-badge-bg text-success-badge-text border-transparent",
        // Warning semantic
        warning:
          "bg-warning-badge-bg text-warning-badge-text border-transparent dark:bg-amber-500 dark:text-grey-800",
        // Danger semantic with light/dark mode support
        danger:
          "bg-danger-100 text-danger-900 dark:bg-danger-500 dark:text-grey-100 border-transparent",
        dangerLight:
          "bg-danger-100 text-danger-900 dark:bg-danger-100 dark:text-danger-900 border-transparent",
        // Outline style respecting tokens
        outline: "border-input bg-background text-foreground",
        visited:
          "bg-[#007A80] text-grey-800 badge-text dark:bg-[#35DFE8]  border-transparent",
        submitted:
          "bg-[#ADE4F7] text-grey-800 badge-text dark:bg-[#56B8D8]  border-transparent",
      },
      size: {
        // Small: 30px height, 14px x-padding, 6px y-padding, 4px radius
        sm: "h-[30px] rounded-[4px] px-[14px] py-[6px] font-inter font-medium text-[12px] leading-[20px] tracking-[0px] align-middle",
        // Medium: 40px height, 20px x-padding, 9px y-padding, 6px radius
        md: "h-[40px] rounded-[6px] px-[20px] py-[9px] font-inter font-medium text-[14px] leading-[20px] tracking-[0px] align-middle",
        // Large: 50px height, 26px x-padding, 12px y-padding, 8px radius
        lg: "h-[50px] rounded-[8px] px-[26px] py-[12px] font-inter font-medium text-[16px] leading-[22px] tracking-[0px] align-middle",
      },
      hasIcon: {
        true: "gap-[10px]",
        false: "gap-[10px]",
      },
    },
    compoundVariants: [
      { size: "sm", hasIcon: false, class: "min-w-[67px]" },
      { size: "sm", hasIcon: true, class: "min-w-[93px]" },
      { size: "md", hasIcon: false, class: "min-w-[85px]" },
      { size: "md", hasIcon: true, class: "min-w-[113px]" },
      { size: "lg", hasIcon: false, class: "min-w-[103px]" },
      { size: "lg", hasIcon: true, class: "min-w-[133px]" },
    ],
    defaultVariants: {
      variant: "default",
      size: "md",
      hasIcon: false,
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  icon?: React.ReactNode;
}

export const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, size, icon, children, ...props }, ref) => {
    const hasIcon = Boolean(icon);
    const iconSizeClass = (() => {
      if (size === "lg") return "h-6 w-6";
      if (size === "md") return "h-5 w-5";
      return "h-4 w-4";
    })();
    return (
      <div
        ref={ref}
        className={cn(badgeVariants({ variant, size, hasIcon }), className)}
        {...props}>
        {hasIcon ? (
          <span className={cn("inline-flex items-center", iconSizeClass)}>
            {icon}
          </span>
        ) : null}
        <span>{children}</span>
      </div>
    );
  }
);
Badge.displayName = "Badge";

export { badgeVariants };
